import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Markup, { bodyClass } from "./circlemarkup";
import { usePage } from "../hooks/usePage";

import {
  getCircles,
  getCircleById,
  createCircle,
  updateCircle,
  clearCircleError,
} from "../store/slices/circle";

import { getSubDivisions } from "../store/slices/subdivision";
import { getOfficers } from "../store/slices/user";

const PageMarkup = memo(Markup);

export default function Circle() {
  usePage({
    title: "All Circles - Nellore Police Dashboard",
    bodyClass,
  });

  const dispatch = useDispatch();

  const {
    circles = [],
    pagination,
    loading,
    error,
    detailsLoading,
    createLoading,
    updateLoading,
  } = useSelector((state) => state.circle);

  const {
    subdivisions = [],
    loading: subdivisionsLoading,
  } = useSelector((state) => state.subdivision);

  const {
    officers = [],
    loading: officersLoading,
  } = useSelector((state) => state.user);

  const total = pagination?.total || 0;

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");

  useEffect(() => {
    dispatch(getCircles({ page: 1, limit: 1000 }));
    dispatch(getSubDivisions({ page: 1, limit: 1000 }));
  }, [dispatch]);

  // Fetch inspectors belonging to the selected Sub-Division
  const handleGetInspectors = useCallback(
    async (subDivisionId) => {
      if (!subDivisionId) return [];

      const response = await dispatch(
        getOfficers({
          designation: "Sub Inspector" || "Circle Inspector",
          active: true,
          subDivisionId,
          page: 1,
          limit: 1000,
        })
      ).unwrap();

      return response?.officers || [];
    },
    [dispatch]
  );

  // Search and status filtering
  const filteredCircles = useMemo(() => {
    const searchText = search.trim().toLowerCase();

    return circles.filter((circle) => {
      const subdivisionName =
        circle.subDivisionId?.name ||
        circle.subDivisionName ||
        "";

      const inspectorName =
        circle.inspectorOfficerId?.displayName ||
        circle.inspectorOfficerId?.username ||
        circle.inspectorName ||
        "";

      const matchesSearch =
        !searchText ||
        [
          circle.name,
          circle.circleId,
          subdivisionName,
          inspectorName,
          circle.phone,
        ].some((value) =>
          String(value || "").toLowerCase().includes(searchText)
        );

      const matchesStatus =
        status === "ALL" ||
        (status === "ACTIVE" && circle.active === true) ||
        (status === "INACTIVE" && circle.active === false);

      return matchesSearch && matchesStatus;
    });
  }, [circles, search, status]);

  // Statistics
  const statistics = useMemo(
    () => ({
      total: total || circles.length,
      active: circles.filter((item) => item.active === true).length,
      inactive: circles.filter((item) => item.active === false).length,
      withInspector: circles.filter(
        (item) => item.inspectorOfficerId || item.inspectorName
      ).length,
    }),
    [circles, total]
  );

  // Fetch Circle details for editing
  const handleGetCircle = async (id) => {
    const response = await dispatch(getCircleById(id)).unwrap();

    return (
      response?.circle ||
      response?.Circle ||
      response?.data?.circle ||
      response?.data?.Circle ||
      response
    );
  };

  // Create Circle
  const handleCreateCircle = async (payload) => {
    await dispatch(createCircle(payload)).unwrap();

    await dispatch(
      getCircles({ page: 1, limit: 1000 })
    ).unwrap();
  };

  // Update Circle
  const handleUpdateCircle = async (id, payload) => {
    await dispatch(updateCircle({ id, payload })).unwrap();

    await dispatch(
      getCircles({ page: 1, limit: 1000 })
    ).unwrap();
  };

  return (
    <PageMarkup
      circles={filteredCircles}
      totalCircles={total || circles.length}
      statistics={statistics}
      loading={loading}
      error={error}
      detailsLoading={detailsLoading}
      saving={createLoading || updateLoading}
      search={search}
      setSearch={setSearch}
      status={status}
      setStatus={setStatus}
      subdivisions={subdivisions}
      subdivisionsLoading={subdivisionsLoading}
      inspectors={officers}
      inspectorsLoading={officersLoading}
      onGetInspectors={handleGetInspectors}
      onGetCircle={handleGetCircle}
      onCreateCircle={handleCreateCircle}
      onUpdateCircle={handleUpdateCircle}
      onAddCircle={() => dispatch(clearCircleError())}
    />
  );
}